/**
 * Version history for an entry (L4 — Google Docs style).
 *
 * Sheet with two views:
 *   · List: local versions (PowerSync bucket, latest 10 offline) +
 *     deep history on demand through the paginated API.
 *   · Detail: word-by-word diff against the CURRENT text + full version
 *     text + "Restore this version" (normal forward write: the current state
 *     is archived as a new version — history is never rewritten).
 */
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, History, Loader2, RotateCcw } from "@fleet/icons";
import { cn, type components } from "@journal/shared";
import type { EntryVersion, JournalEntry } from "@journal/powersync";
import { useEntryVersionsQuery } from "@journal/shared/solid";
import { api } from "@/lib/api";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { WordDiff } from "@/components/ui/word-diff";
import { useI18n } from "@/providers/i18n-provider";
import { formatRelativeTime } from "../format";
import { useJournalData } from "../hooks/use-journal-data";

const PREVIEW_MAX_CHARS = 120;
const LOCAL_SYNC_WINDOW = 10;
const DEEP_PAGE_SIZE = 20;

interface VersionHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryId: () => string;
  /** Current entry (diff reference). */
  entry: () => JournalEntry | null;
  /** Restore: the caller performs the write and closes if appropriate. */
  onRestore: (version: EntryVersion) => void;
  restoring?: () => boolean;
}

/** API row (camelCase, generated types) → local shape (snake_case). */
type ApiVersion = components["schemas"]["EntryVersion"];

function fromApi(row: ApiVersion, userId: string | null): EntryVersion {
  return {
    id: row.id,
    user_id: userId,
    entry_id: row.entryId,
    entry_date: row.entryDate,
    title: row.title ?? null,
    body: row.body,
    mood: row.mood ?? null,
    mood_tags: row.moodTags ?? null,
    word_count: row.wordCount,
    client_ts: row.clientTs ?? null,
    server_ts: row.serverTs,
  };
}

function preview(text: string | null): string {
  const clean = (text ?? "").replaceAll(/\s+/gu, " ").trim();
  return clean.length > PREVIEW_MAX_CHARS ? `${clean.slice(0, PREVIEW_MAX_CHARS)}…` : clean;
}

export function VersionHistorySheet(props: VersionHistorySheetProps) {
  const { t, locale } = useI18n();
  const { db, drizzle, userId, staleTime } = useJournalData();

  const versions = useEntryVersionsQuery({
    db,
    drizzle,
    staleTime,
    entryId: () => props.entryId(),
  });

  // Deep history (API): accumulated pages + cursor.
  const [deepVersions, setDeepVersions] = createSignal<EntryVersion[]>([]);
  const [deepCursor, setDeepCursor] = createSignal<string | null>(null);
  const [deepExhausted, setDeepExhausted] = createSignal(false);
  const [deepLoading, setDeepLoading] = createSignal(false);
  const [selected, setSelected] = createSignal<EntryVersion | null>(null);
  const [view, setView] = createSignal<"changes" | "content">("changes");

  const allVersions = createMemo<EntryVersion[]>(() => {
    const local = versions.data ?? [];
    const seen = new Set(local.map((v) => v.id));
    return [...local, ...deepVersions().filter((v) => !seen.has(v.id))];
  });

  // We only offer "load more" if the local window is full (with fewer than
  // 10 local versions, the ENTIRE history is already on the device).
  const mayHaveDeeper = createMemo(
    () => (versions.data ?? []).length >= LOCAL_SYNC_WINDOW && !deepExhausted(),
  );

  const loadOlder = async () => {
    if (deepLoading()) {
      return;
    }
    setDeepLoading(true);
    try {
      const cursor = deepCursor() ?? allVersions().at(-1)?.id ?? undefined;
      const { data, error } = await api.GET("/api/journal/entry/{id}/versions", {
        params: { path: { id: props.entryId() }, query: { cursor, limit: DEEP_PAGE_SIZE } },
      });
      if (error || !data) {
        return;
      }
      const uid = userId() ?? null;
      setDeepVersions((prev) => [...prev, ...data.versions.map((v) => fromApi(v, uid))]);
      setDeepCursor(data.nextCursor ?? null);
      if (!data.nextCursor) {
        setDeepExhausted(true);
      }
    } catch (error) {
      console.error("Error loading older versions:", error);
    } finally {
      setDeepLoading(false);
    }
  };

  // When it closes (by the user OR programmatically after restoring), the
  // next opening always starts on the LIST, never on a stale detail view.
  createEffect(
    on(
      () => props.open,
      (open) => {
        if (!open) {
          setSelected(null);
          setView("changes");
        }
      },
      { defer: true },
    ),
  );

  const close = (open: boolean) => {
    props.onOpenChange(open);
  };

  return (
    <Dialog open={props.open} onOpenChange={close} contentClass="max-w-lg">
      <div
        class="flex max-h-[70vh] min-h-[40vh] flex-col gap-3 p-1"
        data-testid="version-history-sheet"
      >
        <Show
          when={selected()}
          fallback={
            <>
              <div class="flex items-center gap-2">
                <History class="h-4 w-4 text-primary" />
                <DialogTitle>{t(msg`Version history`)}</DialogTitle>
              </div>
              <p class="text-xs text-muted-foreground">
                {t(msg`A version is saved every time you save changes to this entry.`)}
              </p>

              <div class="flex flex-col gap-2 overflow-y-auto" data-testid="version-list">
                <Show
                  when={!versions.isLoading}
                  fallback={
                    <div class="flex flex-col gap-2">
                      <Skeleton class="h-16 w-full rounded-xl" />
                      <Skeleton class="h-16 w-full rounded-xl" />
                      <Skeleton class="h-16 w-full rounded-xl" />
                    </div>
                  }
                >
                  <Show
                    when={allVersions().length > 0}
                    fallback={
                      <EmptyState
                        icon={History}
                        title={t(msg`No versions yet`)}
                        description={t(
                          msg`Edit this entry and save — your previous words will be kept here.`,
                        )}
                        class="py-6"
                      />
                    }
                  >
                    <For each={allVersions()}>
                      {(version) => (
                        <button
                          type="button"
                          data-testid="version-item"
                          class="flex flex-col gap-1 rounded-xl border border-border/50 bg-muted/20 p-3 text-left transition-colors hover:border-primary/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          onClick={() => setSelected(version)}
                        >
                          <div class="flex items-center justify-between gap-2">
                            <span class="text-xs font-semibold text-foreground">
                              {formatRelativeTime(version.client_ts ?? version.server_ts, locale())}
                            </span>
                            <span class="text-[11px] text-muted-foreground/60">
                              {t(msg`{count} words`, { count: version.word_count ?? 0 })}
                            </span>
                          </div>
                          <Show when={version.title}>
                            <span class="truncate font-display text-sm font-semibold text-foreground/90">
                              {version.title}
                            </span>
                          </Show>
                          <span class="line-clamp-2 font-serif text-xs italic leading-relaxed text-muted-foreground">
                            {preview(version.body)}
                          </span>
                        </button>
                      )}
                    </For>
                    <Show when={mayHaveDeeper()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-muted-foreground"
                        data-testid="version-load-more"
                        disabled={deepLoading()}
                        onClick={() => {
                          void loadOlder();
                        }}
                      >
                        <Show when={deepLoading()}>
                          <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        </Show>
                        {t(msg`Load older versions`)}
                      </Button>
                    </Show>
                  </Show>
                </Show>
              </div>
            </>
          }
        >
          {(version) => (
            <>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t(msg`Back to version list`)}
                  data-testid="version-back"
                  onClick={() => setSelected(null)}
                >
                  <ArrowLeft class="h-4 w-4" />
                </button>
                <DialogTitle class="flex-1 truncate">
                  {formatRelativeTime(version().client_ts ?? version().server_ts, locale())}
                </DialogTitle>
                <div class="flex items-center rounded-lg bg-muted/40 p-0.5 text-xs">
                  <button
                    type="button"
                    data-testid="version-view-changes"
                    class={cn(
                      "rounded-md px-2 py-1 text-muted-foreground",
                      view() === "changes" && "bg-background font-medium text-foreground shadow-sm",
                    )}
                    onClick={() => setView("changes")}
                  >
                    {t(msg`Changes`)}
                  </button>
                  <button
                    type="button"
                    data-testid="version-view-content"
                    class={cn(
                      "rounded-md px-2 py-1 text-muted-foreground",
                      view() === "content" && "bg-background font-medium text-foreground shadow-sm",
                    )}
                    onClick={() => setView("content")}
                  >
                    {t(msg`Full text`)}
                  </button>
                </div>
              </div>

              <div class="flex-1 overflow-y-auto rounded-xl border border-border/40 bg-muted/10 p-3">
                <Show when={version().title || props.entry()?.title}>
                  <div class="mb-2 border-b border-border/30 pb-2">
                    <Show
                      when={view() === "changes"}
                      fallback={
                        <span class="font-display text-base font-semibold">{version().title}</span>
                      }
                    >
                      <WordDiff
                        oldText={version().title ?? ""}
                        newText={props.entry()?.title ?? ""}
                        class="font-display text-base font-semibold"
                      />
                    </Show>
                  </div>
                </Show>
                <Show
                  when={view() === "changes"}
                  fallback={
                    <p
                      class="whitespace-pre-wrap break-words font-serif text-sm leading-[1.75] text-foreground"
                      data-testid="version-content"
                    >
                      {version().body}
                    </p>
                  }
                >
                  <WordDiff oldText={version().body ?? ""} newText={props.entry()?.body ?? ""} />
                </Show>
              </div>

              <p class="text-[11px] leading-relaxed text-muted-foreground/70">
                {t(
                  msg`Restoring never deletes history: what you have now is saved as a new version first.`,
                )}
              </p>
              <div class="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => close(false)}>
                  {t(msg`Cancel`)}
                </Button>
                <Button
                  size="sm"
                  data-testid="version-restore"
                  disabled={props.restoring?.() === true}
                  onClick={() => {
                    props.onRestore(version());
                  }}
                >
                  <Show
                    when={props.restoring?.() === true}
                    fallback={<RotateCcw class="mr-1.5 h-3.5 w-3.5" />}
                  >
                    <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  </Show>
                  {t(msg`Restore this version`)}
                </Button>
              </div>
            </>
          )}
        </Show>
      </div>
    </Dialog>
  );
}
